import type { ConditionRule, Role } from '@servicetim/shared';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@servicetim/ui/components';
import { Plus, Trash2 } from 'lucide-react';
import type { FieldCatalog } from '../projects/catalog/field-catalog';
import { removeAt, replaceAt } from '../projects/lib/list-utils';
import { ConditionBuilder, newLeaf } from '../workflows/rules/condition-builder';

const LEAF_TYPES = [
  'has_permission',
  'user_is',
  'user_in_role',
  'user_in_group',
  'field_matches',
] as const;
const LEAF_LABELS: Record<(typeof LEAF_TYPES)[number], string> = {
  has_permission: 'User has permission',
  user_is: 'User is…',
  user_in_role: 'User holds project role',
  user_in_group: 'User is in group',
  field_matches: 'Field matches',
};

export interface RuleConditionsEditorProps {
  /** `automation_rule.conditions`: an array ANDed together (S-07-02; `[]` = always). */
  value: readonly ConditionRule[];
  onChange: (next: ConditionRule[]) => void;
  catalog: FieldCatalog;
  roles: readonly Role[];
  idPrefix: string;
}

/**
 * Top-level list of `ConditionRule`s, all of which must hold. Unlike `ConditionBuilder` (which
 * edits *one* `all`/`any`/`not`/leaf node), the API stores the rule's conditions as a bare array
 * — so this renders each array entry with its own `ConditionBuilder` (any of them can still be
 * an `any`/`not` group internally) instead of wrapping the array in a synthetic top-level group.
 */
export function RuleConditionsEditor({
  value,
  onChange,
  catalog,
  roles,
  idPrefix,
}: RuleConditionsEditorProps) {
  return (
    <div className="space-y-2">
      {value.length === 0 ? (
        <p className="text-fg-muted text-sm">No conditions: the rule always matches.</p>
      ) : (
        <ul className="space-y-2">
          {value.map((item, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: rows carry no identity; position is the key
            <li key={`${idPrefix}-${index}`} className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <ConditionBuilder
                  value={item}
                  onChange={(next) => onChange(replaceAt(value, index, next))}
                  catalog={catalog}
                  roles={roles}
                  idPrefix={`${idPrefix}-${index}`}
                  depth={1}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remove condition ${index + 1}`}
                onClick={() => onChange(removeAt(value, index))}
              >
                <Trash2 />
              </Button>
            </li>
          ))}
        </ul>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline" size="sm" disabled={value.length >= 20}>
            <Plus />
            Add condition
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuLabel>Condition</DropdownMenuLabel>
          {LEAF_TYPES.map((type) => (
            <DropdownMenuItem
              key={type}
              onSelect={() => onChange([...value, newLeaf(type, catalog, roles)])}
            >
              {LEAF_LABELS[type]}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Group</DropdownMenuLabel>
          <DropdownMenuItem
            onSelect={() =>
              onChange([...value, { any: [newLeaf('has_permission', catalog, roles)] }])
            }
          >
            Any of…
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => onChange([...value, { not: newLeaf('user_is', catalog, roles) }])}
          >
            Not…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
